import React, { useEffect, useState } from "react";
import { Button } from "../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../../design/dialog";
import { Input } from "../../design/input";
import { Field, Fieldset, FieldGroup, Label } from "../../design/fieldset";
import { Textarea } from "../../design/textarea";
import { useParentingPlan } from "../../contexts/ParentingPlanContext";
import { PlusIcon } from "@heroicons/react/20/solid";
import { DocumentGenerationMethod } from "../../models/DocumentGenerationMethod";
import ChooseGenerationMethod from "./components/ChooseGenerationMethod";
import { Subheading } from "../../design/heading";

interface Props {
  generationMethod?: DocumentGenerationMethod;
  className?: string;
  children?: React.ReactNode;
}

const CreateParentingPlanButton: React.FC<Props> = (props) => {
  const { createParentingPlan } = useParentingPlan();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [generationMethod, setGenerationMethod] = useState<
    DocumentGenerationMethod | undefined
  >(props.generationMethod);
  const [description, setDescription] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const handleSubmit = async () => {
    if (!name) return;
    setIsUploading(true);
    try {
      await createParentingPlan({
        name,
        description,
        generationMethod: generationMethod ?? "manual",
        status: "draft",
        effectiveDate: new Date(effectiveDate),
        expirationDate: expirationDate ? new Date(expirationDate) : undefined,
      });
      clearForm();
    } catch (err) {
      console.error("Error creating document:", err);
      alert("Something went wrong. Please try again.");
    }
    setIsUploading(false);
  };

  const clearForm = () => {
    setName("");
    setDescription("");
    setGenerationMethod(undefined);
    setIsOpen(false);
    setIsUploading(false);
  };

  return (
    <>
      <Button
        className={props.className}
        type="button"
        onClick={() => setIsOpen(true)}
      >
        {props.children ?? (
          <>
            <PlusIcon className="w-4 h-4" />
            <span className="hidden sm:block">Create Parenting Plan</span>
          </>
        )}
      </Button>

      <Dialog open={isOpen} onClose={setIsOpen}>
        <DialogTitle>Create a Parenting Plan</DialogTitle>
        <DialogDescription>
          <div className="p-4 border border-blue-300 rounded-md bg-blue-50">
            <Subheading level={4} className="mb-1 text-blue-900">
              Want expert help crafting your plan?
            </Subheading>
            <p className="mb-3 text-xs text-blue-800">
              A professional can guide you through a complete, court-ready
              parenting plan — especially helpful if your situation is complex
              or contested.
            </p>
            <Button color="blue" href="/professionals">
              Work with a professional
            </Button>
          </div>
        </DialogDescription>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name"
                />
              </Field>
              <Field>
                <Label>Description</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description"
                />
              </Field>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>Effective Date</Label>
                  <Input
                    type="date"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>Expiration Date</Label>
                  <Input
                    type="date"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                  />
                </Field>
              </div>
            </FieldGroup>
          </Fieldset>
        </DialogBody>

        <DialogActions>
          <Button plain onClick={clearForm}>
            Cancel
          </Button>
          <Button
            color="green"
            disabled={!name || isUploading}
            onClick={handleSubmit}
          >
            {isUploading ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateParentingPlanButton;
